import React, { useEffect, useRef, useState } from "react";
import * as Yup from "yup";
import { useFormik } from "formik";
import { Button } from "primereact/button";
import { InputText as PrimeInputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Checkbox } from "primereact/checkbox";
import { useNavigate, useParams } from "react-router-dom";
import { Card } from "primereact/card";
import apiFunctions from "../../services/api-service";
import useToast from "../../components/common/useToast";
import { AxiosError } from "axios";
import { Select, Spin } from "antd";
import { errorHandler } from "../../utils/HelperFunctions";
import { ArrowLeftIcon, DocumentTextIcon } from "@heroicons/react/24/outline";
import { useJsApiLoader } from "@react-google-maps/api";

const { Option } = Select;

const GOOGLE_LIBRARIES: ("places")[] = ["places"];

interface FormValues {
    userid: string;
    designation: string;
    companyName: string;
    jobLocation: string;
    joblocationcity: string;
    noOfVacancies: string;
    education: string;
    experience: string;
    roleResponsibility: string;
    salary: string;
    contactPersonName: string;
    contactPersonMobile: string;
    contactPersonEmail: string;
    isactive: boolean;
}

const JobAdd: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { showToast }: any = useToast();

    const { isLoaded } = useJsApiLoader({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAP_KEY || "",
        libraries: GOOGLE_LIBRARIES,
    });
    const locationInputRef = useRef<HTMLInputElement | null>(null);

    const [loading, setLoading] = useState(false);

    const [userOptions, setUserOptions] = useState<any[]>([]);
    const [userSearchValue, setUserSearchValue] = useState("");
    const [fetchingUsers, setFetchingUsers] = useState(false);
    const [firstUser, setFirstUser] = useState(0);
    const [totalUsersCount, setTotalUsersCount] = useState(0);
    const fetchingUsersRef = useRef(false);
    const ownerOptionRef = useRef<any>(null);
    const rows = 10;

    const dedupeUsers = (list: any[]) =>
        list.filter((u, i, arr) => arr.findIndex(x => (x.id || x._id) === (u.id || u._id)) === i);

    const [logoFile, setLogoFile] = useState<File | null>(null);
    const [logoPreview, setLogoPreview] = useState<string | null>(null);
    const logoInputRef = useRef<HTMLInputElement | null>(null);

    const formik = useFormik<FormValues>({
        initialValues: {
            userid: "",
            designation: "",
            companyName: "",
            jobLocation: "",
            joblocationcity: "",
            noOfVacancies: "1",
            education: "",
            experience: "",
            roleResponsibility: "",
            salary: "",
            contactPersonName: "",
            contactPersonMobile: "",
            contactPersonEmail: "",
            isactive: true,
        },
        validationSchema: Yup.object({
            userid: Yup.string().required("Owner is required"),
            designation: Yup.string().required("Designation is required"),
            contactPersonName: Yup.string().required("Contact person name is required"),
            contactPersonMobile: Yup.string().required("Contact person mobile is required"),
            contactPersonEmail: Yup.string().email("Invalid email"),
        }),
        onSubmit: async (values) => {
            try {
                setLoading(true);

                const formData = new FormData();
                Object.entries(values).forEach(([key, value]) => {
                    formData.append(key, String(value));
                });
                if (logoFile) formData.append("companyLogo", logoFile);

                if (id) {
                    const response = await apiFunctions.put(`/job/admin/update/${id}`, formData, undefined, true);
                    if (response?.data?.success) {
                        showToast({ severity: "success", summary: "Updated", detail: "Job updated successfully", life: 3000 });
                        navigate("/admin/jobdesc");
                    } else {
                        showToast({ severity: "error", summary: "Error", detail: response?.data?.message || "Update failed", life: 3000 });
                    }
                } else {
                    const response = await apiFunctions.post("/job/admin/create", formData, null, true);
                    if (response?.data?.success) {
                        showToast({ severity: "success", summary: "Created", detail: "Job created successfully", life: 3000 });
                        navigate("/admin/jobdesc");
                    } else {
                        showToast({ severity: "error", summary: "Error", detail: response?.data?.message || "Create failed", life: 3000 });
                    }
                }
            } catch (err) {
                errorHandler(err as AxiosError);
            } finally {
                setLoading(false);
            }
        },
    });

    const fetchUsers = async (reset = false) => {
        if (fetchingUsersRef.current) return;
        fetchingUsersRef.current = true;
        setFetchingUsers(true);
        const skip = reset ? 0 : firstUser;
        try {
            let url = `/user/dropdownlist/${skip}/${rows}`;
            if (userSearchValue && userSearchValue.length > 1) url += `/${userSearchValue}`;
            const response = await apiFunctions.get(url);
            const newItems = response?.data?.data || [];
            const total = response?.data?.totalRecords || 0;
            if (reset) {
                setUserOptions(dedupeUsers([
                    ...(ownerOptionRef.current ? [ownerOptionRef.current] : []),
                    ...newItems
                ]));
                setFirstUser(rows);
            } else {
                setUserOptions(prev => dedupeUsers([...prev, ...newItems]));
                setFirstUser(prev => prev + rows);
            }
            setTotalUsersCount(total);
        } finally {
            fetchingUsersRef.current = false;
            setFetchingUsers(false);
        }
    };

    useEffect(() => {
        setFirstUser(0);
        fetchUsers(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userSearchValue]);

    // Google Places autocomplete on Job Location field
    useEffect(() => {
        if (!isLoaded || !locationInputRef.current) return;
        const timer = setTimeout(() => {
            if (!locationInputRef.current) return;
            const autocomplete = new (window as any).google.maps.places.Autocomplete(
                locationInputRef.current
            );
            autocomplete.addListener("place_changed", () => {
                const place = autocomplete.getPlace();
                if (!place.place_id) return;
                const components = place.address_components || [];
                const getComp = (type: string) =>
                    components.find((c: any) => c.types.includes(type))?.long_name || "";
                formik.setFieldValue("jobLocation", place.formatted_address || place.name || "");
                formik.setFieldValue("joblocationcity", getComp("locality") || getComp("administrative_area_level_2"));
            });
        }, 100);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoaded]);

    const handleUserScroll = (e: any) => {
        const { scrollTop, offsetHeight, scrollHeight } = e.target;
        if (scrollTop + offsetHeight >= scrollHeight - 10 && userOptions.length < totalUsersCount && !fetchingUsers) {
            fetchUsers();
        }
    };

    // Edit mode: load existing job
    useEffect(() => {
        if (!id) return;
        (async () => {
            try {
                const response = await apiFunctions.get(`/job/admin/${id}`);
                if (response?.data?.success) {
                    const j = response.data.data;
                    formik.setValues({
                        userid: j.userid || "",
                        designation: j.designation || "",
                        companyName: j.companyName || "",
                        jobLocation: typeof j.jobLocation === "string" ? j.jobLocation : (j.jobLocation?.formattedAddress || ""),
                        joblocationcity: j.joblocationcity || "",
                        noOfVacancies: j.noOfVacancies ? String(j.noOfVacancies) : "1",
                        education: j.education || "",
                        experience: j.experience || "",
                        roleResponsibility: j.roleResponsibility || "",
                        salary: j.salary || "",
                        contactPersonName: j.contactPersonName || "",
                        contactPersonMobile: j.contactPersonMobile || "",
                        contactPersonEmail: j.contactPersonEmail || "",
                        isactive: !!j.isactive,
                    });
                    if (j.companyLogoUrl) setLogoPreview(j.companyLogoUrl);
                    if (j.userid && j.ownerName) {
                        ownerOptionRef.current = { id: j.userid, name: j.ownerName };
                        setUserOptions(prev =>
                            prev.some(u => (u.id || u._id) === j.userid)
                                ? prev
                                : [ownerOptionRef.current, ...prev]
                        );
                    }
                }
            } catch (err) {
                errorHandler(err as AxiosError);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setLogoFile(file);
            setLogoPreview(URL.createObjectURL(file));
        }
    };

    const fieldError = (name: keyof FormValues) =>
        formik.touched[name] && formik.errors[name] ? (
            <small className="p-error block mt-1">{formik.errors[name] as string}</small>
        ) : null;

    return (
        <div className="unlist-margin">
            <div className="back-button mb-2" onClick={() => navigate("/admin/jobdesc")}>
                <ArrowLeftIcon className="w-6 h-6 text-black-700" /><h3 className="m-0">Back</h3>
            </div>

            <Card title={id ? "Edit Job" : "Add Job"}>
                <form onSubmit={formik.handleSubmit}>
                    <div className="grid grid-cols-12 gap-4">

                        {/* Owner */}
                        <div className="col-span-12 lg:col-span-6">
                            <label className="form-label">Owner (User) *</label>
                            <Select
                                showSearch
                                allowClear
                                className="w-full h-[42px] mt-1"
                                placeholder="Select owner"
                                filterOption={false}
                                onSearch={(v) => setUserSearchValue(v)}
                                onPopupScroll={handleUserScroll}
                                notFoundContent={fetchingUsers ? <Spin size="small" /> : "No users"}
                                value={formik.values.userid || undefined}
                                onChange={(value) => formik.setFieldValue("userid", value || "")}
                            >
                                {userOptions.map(user => (
                                    <Option key={user.id} value={user.id}>
                                        {user.name}{user.mobileno ? ` - ${user.mobileno}` : ""}
                                    </Option>
                                ))}
                            </Select>
                            {fieldError("userid")}
                        </div>

                        <div className="col-span-12 lg:col-span-6">
                            <label className="form-label">Designation *</label>
                            <PrimeInputText className="w-full mt-1" value={formik.values.designation}
                                onChange={(e) => formik.setFieldValue("designation", e.target.value)} />
                            {fieldError("designation")}
                        </div>

                        <div className="col-span-12 lg:col-span-6">
                            <label className="form-label">Company Name</label>
                            <PrimeInputText className="w-full mt-1" value={formik.values.companyName}
                                onChange={(e) => formik.setFieldValue("companyName", e.target.value)} />
                        </div>

                        <div className="col-span-12 lg:col-span-3">
                            <label className="form-label">No. of Vacancies</label>
                            <PrimeInputText className="w-full mt-1" keyfilter="int" value={formik.values.noOfVacancies}
                                onChange={(e) => formik.setFieldValue("noOfVacancies", e.target.value)} />
                        </div>

                        <div className="col-span-12 lg:col-span-3">
                            <label className="form-label">Salary</label>
                            <PrimeInputText className="w-full mt-1" placeholder="e.g. ₹20,000 - ₹30,000 / month" value={formik.values.salary}
                                onChange={(e) => formik.setFieldValue("salary", e.target.value)} />
                        </div>

                        <div className="col-span-12 lg:col-span-6">
                            <label className="form-label">Job Location</label>
                            <PrimeInputText
                                ref={locationInputRef as any}
                                className="w-full mt-1"
                                placeholder="Search location on Google Maps..."
                                value={formik.values.jobLocation}
                                onChange={(e) => formik.setFieldValue("jobLocation", e.target.value)}
                            />
                        </div>

                        <div className="col-span-12 lg:col-span-6">
                            <label className="form-label">City</label>
                            <PrimeInputText className="w-full mt-1" value={formik.values.joblocationcity}
                                onChange={(e) => formik.setFieldValue("joblocationcity", e.target.value)} />
                        </div>

                        <div className="col-span-12 lg:col-span-6">
                            <label className="form-label">Education</label>
                            <PrimeInputText className="w-full mt-1" value={formik.values.education}
                                onChange={(e) => formik.setFieldValue("education", e.target.value)} />
                        </div>

                        <div className="col-span-12 lg:col-span-6">
                            <label className="form-label">Experience</label>
                            <PrimeInputText className="w-full mt-1" placeholder="e.g. 2-5 years or Fresher" value={formik.values.experience}
                                onChange={(e) => formik.setFieldValue("experience", e.target.value)} />
                        </div>

                        <div className="col-span-12">
                            <label className="form-label">Role & Responsibility</label>
                            <InputTextarea className="w-full mt-1" rows={3} value={formik.values.roleResponsibility}
                                onChange={(e) => formik.setFieldValue("roleResponsibility", e.target.value)} />
                        </div>

                        <div className="col-span-12 lg:col-span-4">
                            <label className="form-label">Contact Person Name *</label>
                            <PrimeInputText className="w-full mt-1" value={formik.values.contactPersonName}
                                onChange={(e) => formik.setFieldValue("contactPersonName", e.target.value)} />
                            {fieldError("contactPersonName")}
                        </div>

                        <div className="col-span-12 lg:col-span-4">
                            <label className="form-label">Contact Person Mobile *</label>
                            <PrimeInputText className="w-full mt-1" value={formik.values.contactPersonMobile}
                                onChange={(e) => formik.setFieldValue("contactPersonMobile", e.target.value)} />
                            {fieldError("contactPersonMobile")}
                        </div>

                        <div className="col-span-12 lg:col-span-4">
                            <label className="form-label">Contact Person Email</label>
                            <PrimeInputText className="w-full mt-1" value={formik.values.contactPersonEmail}
                                onChange={(e) => formik.setFieldValue("contactPersonEmail", e.target.value)} />
                            {fieldError("contactPersonEmail")}
                        </div>

                        {/* Company Logo */}
                        <div className="col-span-12 lg:col-span-6">
                            <label className="form-label">Company Logo</label>
                            <div className="flex items-center gap-3 mt-1">
                                {logoPreview ? (
                                    <img src={logoPreview} alt="logo" style={{ width: "64px", height: "64px", objectFit: "cover", borderRadius: "8px", border: "1px solid #e5e7eb" }} />
                                ) : (
                                    <div style={{ width: "64px", height: "64px", borderRadius: "8px", border: "1px dashed #d1d5db", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                        <DocumentTextIcon style={{ width: "28px", height: "28px", color: "#d1d5db" }} />
                                    </div>
                                )}
                                <Button type="button" label="Choose Logo" outlined onClick={() => logoInputRef.current?.click()} />
                                <input ref={logoInputRef} type="file" accept="image/*" hidden onChange={handleLogoChange} />
                            </div>
                        </div>

                        {/* Active */}
                        <div className="col-span-12 flex items-center gap-2">
                            <Checkbox inputId="isactive" checked={formik.values.isactive}
                                onChange={(e) => formik.setFieldValue("isactive", e.checked)} />
                            <label htmlFor="isactive" className="form-label m-0 cursor-pointer">Active</label>
                        </div>

                        <div className="col-span-12 flex justify-end gap-2 mt-2">
                            <Button type="button" label="Cancel" severity="secondary" onClick={() => navigate("/admin/jobdesc")} disabled={loading} />
                            <Button type="submit" label={id ? "Update" : "Create"} loading={loading} />
                        </div>

                    </div>
                </form>
            </Card>
        </div>
    );
};

export default JobAdd;
